import type { ApolloError } from "@apollo/client";
import type { NetworkError } from "@apollo/client/errors";
import type { GraphQLError, GraphQLFormattedError } from "graphql";

export type GraphQLErrorResponse = { error: ApolloError | undefined };
export type ErrorSideEffectHandler = (props: GraphQLErrorResponse) => void;
export type GraphQLErrors = readonly GraphQLFormattedError[];
/**
 * バックエンドで定義されている共通のエラー
 */
export interface ChillnnGraphQLError extends GraphQLError {
	extensions: {
		chillnnErrorMessageForDeveloper?: string;
		chillnnErrorMessageForUser: { ja: string; en: string };
		chillnnStatusCode: number;
		code: string;
	};
}

/**
 * ServerError の body に載せられた GraphQL errors を取り出す
 * constraint 違反などは HTTP 400 として networkError 側に入ることがある
 */
const filterGraphQLErrorsFromNetworkError = (networkError: NetworkError | null | undefined): GraphQLErrors => {
	if (!networkError || typeof networkError !== "object" || !("result" in networkError)) {
		return [];
	}

	const result = networkError.result;
	if (!result || typeof result !== "object" || !("errors" in result) || !Array.isArray(result.errors)) {
		return [];
	}

	return result.errors.filter((error): error is GraphQLFormattedError => {
		return !!error && typeof error === "object" && "message" in error;
	});
};

/**
 * 副作用ハンドラ向けに graphQLErrors と networkError を確定する
 * body に GraphQL errors がある HTTP エラーは networkError として扱わない
 */
export const getGraphQLErrorsAndNetworkError = (
	error: ApolloError,
): {
	graphQLErrors: GraphQLErrors;
	networkError: NetworkError | null;
} => {
	const graphQLErrors = [...error.graphQLErrors, ...filterGraphQLErrorsFromNetworkError(error.networkError)];

	return {
		graphQLErrors,
		networkError: error.networkError && graphQLErrors.length === 0 ? error.networkError : null,
	};
};
